// src/utils/offerEngine.js
import * as offersService from '../services/offersService';
import * as chatService from '../services/chatService';
import * as notificationsService from '../services/notificationsService';
import { productRepository } from '../repositories/productRepository';
import { db } from "../lib/firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "../lib/firestoreClient";
import { warnDev } from "../utils/logger";
import { errorDev } from './logger';

const CHATS = 'chats';

async function loadUserName(uid) {
  try {
    if (!uid) return null;
    const snap = await getDoc(doc(db, "users", String(uid)));
    if (!snap.exists()) return null;
    const d = snap.data();
    return d.displayName || d.name || d.email || null;
  } catch (e) {
    // safe fallback
    return null;
  }
}

async function loadProductTitle(pid) {
  const product = await productRepository.getById(pid);
  return product?.title || product?.name || null;
}

async function loadProductById(pid) {
  return productRepository.getById(pid);
}

/**
 * best-effort update to chat-level metadata.
 * It's not required for correctness, but helps the inbox preview and
 * avoids relying on mutating message docs (which your rules forbid).
 */
async function safeUpdateChatMeta(chatId, updates = {}) {
  if (!chatId) return;
  try {
    const ref = doc(db, CHATS, String(chatId));
    // ensure we update updatedAt server-side
    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
  } catch (e) {
    // Don't spam the console. Only warn succinctly.
    if (e?.code === 'permission-denied') {
      warnDev(`safeUpdateChatMeta: permission denied writing chat ${chatId}`);
    } else {
      warnDev(`safeUpdateChatMeta: failed updating chat ${chatId}`, e?.message || e);
    }
  }
}

/**
 * createOffer:
 * - ensures a chat exists (creates if needed),
 * - writes the offer document (sellerId/buyerId always present),
 * - posts an in-chat offer message (type 'offer'),
 * - creates a friendly notification,
 * - updates chat meta (lastOfferAmount/lastOfferId) best-effort.
 *
 * returns { offer, chatId } (throws on invalid input).
 */
export const createOffer = async ({
  chatId = null,
  productId,
  fromUserId,
  toUserId,
  amount,
  message = '',
  metadata = {},
  counterToOfferId = null,
}) => {
  // sanitize inputs
  const buyerId = fromUserId ? String(fromUserId) : null;
  const sellerId = toUserId ? String(toUserId) : null;
  const pid = chatService.sanitizeId(productId) || productId || null;

  // For counter offers, preserve the original buyer/seller roles regardless of who is countering
  let resolvedBuyerId = buyerId;
  let resolvedSellerId = sellerId;
  let normalizedCounterToOfferId = counterToOfferId ? String(counterToOfferId) : null;
  if (normalizedCounterToOfferId) {
    try {
      const original = await offersService.getOfferById(normalizedCounterToOfferId);
      if (original?.buyerId && original?.sellerId) {
        resolvedBuyerId = String(original.buyerId);
        resolvedSellerId = String(original.sellerId);
      }
    } catch (_) {}
  }

  // coerce amount to a number (FireStore rules usually expect number for amount)
  const amtNum = Number(amount);
  if (!Number.isFinite(amtNum)) {
    throw new Error('Invalid offer amount. Amount must be numeric.');
  }

  const productForValidation = await loadProductById(pid);
  const sellerPrice = Number(productForValidation?.price);
  if (Number.isFinite(sellerPrice) && sellerPrice > 0) {
    const minAllowed = sellerPrice * 0.4;
    if (amtNum > sellerPrice) {
      throw new Error(`Offer cannot exceed seller price (${sellerPrice.toLocaleString()} RWF).`);
    }
    if (amtNum < minAllowed) {
      throw new Error(`Offer must be at least 40% of seller price (${Math.ceil(minAllowed).toLocaleString()} RWF).`);
    }
  }

  // ensure chat exists so offer doc contains chatId
  let cid = chatId;
  if (!cid) {
    try {
      const r = await chatService.findOrCreateChat(resolvedBuyerId, resolvedSellerId, pid);
      cid = r?.chatId || r?.id || null;
    } catch (e) {
      warnDev("createOffer: failed to create/find chat (proceeding without chat)");
      cid = null;
    }
  }

  // create offer document with numeric amount
  const offerMetadata = {
    ...(metadata && typeof metadata === 'object' ? metadata : {}),
    ...(normalizedCounterToOfferId ? { counterToOfferId: normalizedCounterToOfferId } : {}),
  };

  const offer = await offersService.createOffer({
    productId: pid,
    buyerId: resolvedBuyerId,
    sellerId: resolvedSellerId,
    amount: amtNum,
    message,
    chatId: cid || null,
    metadata: offerMetadata,
  });
  // friendly strings for notification & chat text
  const [buyerName, productTitle] = await Promise.all([
    loadUserName(buyerId).catch(() => null),
    loadProductTitle(pid).catch(() => null),
  ]);

  const prettyAmt = Number.isFinite(Number(amtNum)) ? `${Number(amtNum).toLocaleString()} RWF` : `${amtNum} RWF`;
  const chatText = message ? String(message) : '';

  // When countering, include the original offer amount so the UI can show strikethrough pricing
  let extraOriginalPrice = normalizedCounterToOfferId ? null : undefined;
  if (normalizedCounterToOfferId) {
    try {
      const original = await offersService.getOfferById(normalizedCounterToOfferId);
      if (original?.amount != null) extraOriginalPrice = Number(original.amount);
    } catch (_) {}
  }

  // post in-chat message (if chat exists) — chatService.sendMessage is tolerant
  if (cid) {
    try {
      const sanitizedCid = chatService.sanitizeId(cid) || cid;
      await chatService.sendMessage({
        chatId: sanitizedCid,
        senderId: buyerId,
        text: chatText,
        type: 'offer',
        extra: {
          offerId: offer.id || null,
          offerAmount: amtNum,
          offerStatus: 'pending',
          ...(normalizedCounterToOfferId ? { counterToOfferId: normalizedCounterToOfferId } : {}),
          ...(extraOriginalPrice != null ? { originalPrice: extraOriginalPrice } : {}),
        }
      });
    } catch (e) {
      warnDev("createOffer: failed to post in-chat message (non-fatal)");
    }

    // update chat-level meta to help inbox previews (best-effort)
    try {
      await safeUpdateChatMeta(cid, { lastOfferAmount: amtNum, lastOfferId: offer.id || null });
    } catch (_) { /* already handled in safeUpdateChatMeta */ }
  }

  // friendly notification
  const notifContent = `${buyerName || 'Someone'} sent an offer of ${prettyAmt}${productTitle ? ` for ${productTitle}` : ''}.`;
  try {
    await notificationsService.createNotification({
      toUserId: sellerId,
      fromUserId: buyerId,
      type: 'offer',
      content: notifContent,
      payload: { offerId: offer.id || null, productId: pid, productTitle, buyerName }
    });
  } catch (e) {
    warnDev("createOffer: notification creation failed (non-fatal)");
  }

  return { offer, chatId: cid };
};

/**
 * respondToOffer:
 * - updates the offer doc status (throws if permission denied),
 * - refetches the offer,
 * - posts notification for other party,
 * - posts a system message in the chat,
 * - updates chat-level meta (lastOfferStatus/lastOfferId) best-effort.
 *
 * returns the updated offer object, or null if not found.
 */
export const respondToOffer = async (offerId, status, actorId) => {
  if (!offerId) throw new Error("respondToOffer: offerId required");
  if (!actorId) throw new Error("respondToOffer: actorId required");

  // update the offer doc (this enforces security rules on server)
  await offersService.updateOfferStatus(offerId, status, actorId);

  // re-read the offer
  const offer = await offersService.getOfferById(offerId);
  if (!offer) return null;

  const otherUser = String(actorId) === String(offer.sellerId) ? offer.buyerId : offer.sellerId;

  // Compose friendlier notification; include product title if available
  const productTitle = await loadProductTitle(offer.productId);
  const actorName = await loadUserName(actorId);
  const amt = Number.isFinite(Number(offer.amount)) ? `${Number(offer.amount).toLocaleString()} RWF` : `${offer.amount} RWF`;

  const friendly = status === 'accepted'
    ? `${actorName || 'Someone'} accepted an offer of ${amt}${productTitle ? ` for ${productTitle}` : ''}.`
    : `${actorName || 'Someone'} declined an offer of ${amt}${productTitle ? ` for ${productTitle}` : ''}.`;

  try {
    await notificationsService.createNotification({
      toUserId: otherUser,
      fromUserId: actorId,
      type: `offer:${status}`,
      content: friendly,
      payload: { offerId, productId: offer.productId, productTitle, amount: offer.amount },
    });
  } catch (e) {
    warnDev("respondToOffer: notification side-effect failed (non-fatal)");
  }

  // send chat system message side-effect — prefer offer.chatId, else try to find/create chat
  try {
    let chatId = offer.chatId;
    if (!chatId) {
      try {
        const r = await chatService.findOrCreateChat(offer.buyerId, offer.sellerId, offer.productId);
        chatId = r?.chatId || r?.id || null;
      } catch (e) {
          warnDev("respondToOffer: could not find/create chat as fallback (non-fatal)");
        chatId = null;
      }
    }

    if (chatId) {
      const sanitizedCid = chatService.sanitizeId(chatId) || chatId;
      
      // For accepted offers, send manual coordination guidance and seller confirmation
      if (status === 'accepted') {
        try {
          const buyerMessage = `Offer accepted. Let's plan the payment method and delivery.`;

          await chatService.sendMessage({
            chatId: sanitizedCid,
            senderId: actorId,
            text: buyerMessage,
            type: 'payment_request',
            extra: { 
              offerId, 
              status,
              buyerId: offer.buyerId,
              sellerId: offer.sellerId,
              amount: offer.amount,
              productId: offer.productId,
              productTitle,
              dealStatus: 'accepted'
            }
          });
        } catch (e) {
          warnDev("Accepted-offer guidance message failed (non-fatal):", e?.message);
        }
      }
      
      // System message for status change — only to the other party, never to the actor
      if (status === 'declined') {
        const otherPartyId = String(actorId) === String(offer.sellerId) ? offer.buyerId : offer.sellerId;
        if (otherPartyId) {
          try {
            await chatService.sendMessage({
              chatId: sanitizedCid,
              senderId: actorId,
              text: `${actorName || 'Someone'} declined an offer of ${amt}${productTitle ? ` for ${productTitle}` : ''}.`,
              type: 'system',
              extra: { offerId, status, declinedBy: actorId }
            });
          } catch (e) {
            warnDev("Declined-offer system message failed (non-fatal):", e?.message);
          }
        }
      }

      // best-effort update of chat meta to help inbox previews and conversation UIs
      try {
        await safeUpdateChatMeta(chatId, { lastOfferId: offerId, lastOfferStatus: status });
      } catch (_) { /* noop, safeUpdateChatMeta logs */ }
    }
  } catch (e) {
    warnDev("respondToOffer: chat side-effect failed (non-fatal)");
  }

  return offer;
};


